/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRecaptchaV3 } from '../components/CaptchaComponent';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const makeApiPath = (path: string) => {
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { executeRecaptcha } = useRecaptchaV3();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Execute reCAPTCHA v3 for password reset action
      const captchaToken = await executeRecaptcha('forgot_password');
      
      await axios.post(makeApiPath('/auth/forgot-password'), { 
        email,
        captchaToken
      });
      
      setSuccess(true);
    } catch (err: any) {
      logger.error('Forgot password error:', err);
      if (err.response?.status === 429) {
        const retryAfter = err.response?.data?.retryAfter;
        const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 5;
        const errorMessage = t('passwordResetRateLimit', { minutes });
        const fixedMessage = errorMessage.replace(/{minutes}/g, minutes.toString());
        setError(fixedMessage);
      } else {
        setError(err.response?.data?.message || t('errorOccurredTryAgain'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 'calc(100vh - 140px)' }}>
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              {t('checkYourEmail')}
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('passwordResetEmailSent')}
            </Alert>
            
            <Typography variant="body1" align="center" paragraph>
              {t('checkEmailInboxInstruction')}
            </Typography>
            
            <Box mt={3} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{ mr: 2 }}
              >
                {t('backToLogin')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
              >
                {t('sendAnotherEmail')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 'calc(100vh - 140px)' }}>
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {t('forgotPassword')}
          </Typography>
          
          <Typography variant="body1" align="center" paragraph>
            {t('enterYourEmailInstruction')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              label={t('emailAddress')}
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              style={{ marginTop: '1.5rem' }}
              disabled={isSubmitting || !email}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('sendResetLink')}
            </Button>
          </form>
          
          <Box mt={3} textAlign="center">
            <Typography variant="body2">
              {t('rememberYourPassword')}{' '}
              <Link component={RouterLink} to="/login">
                {t('login')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ForgotPasswordPage; 